import { useEffect, useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";

// TYPES
type SettingsType = {
  dsrReport: boolean;

  lfrInclRate: boolean;
  lfrProductTotal: boolean;
  dealerCommission: boolean;
  requireInvoiceNo: boolean;

  statementNo: boolean;
  statementName: boolean;
  noDescription: boolean;
  noMisc: boolean;
  noIndentBill: boolean;
  terms: boolean;

  swipeRecovery: boolean;
};

type SectionKey = "dsr" | "invoice" | "statement" | "recovery";

export default function SystemSettings() {
  const [open, setOpen] = useState<SectionKey | null>(null);

  const [settings, setSettings] = useState<SettingsType>({
    dsrReport: false,

    lfrInclRate: false,
    lfrProductTotal: false,
    dealerCommission: false,
    requireInvoiceNo: false,

    statementNo: false,
    statementName: false,
    noDescription: false,
    noMisc: false,
    noIndentBill: false,
    terms: false,

    swipeRecovery: false,
  });

  // FETCH
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const res = await fetch("/api/system-settings");
    const data = await res.json();
    setSettings(data);
  };

  // TOGGLE CHECKBOX
  const toggle = (key: keyof SettingsType) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // SAVE
  const handleSave = async () => {
    await fetch("/api/system-settings", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    });

    alert("Settings Updated");
  };

  // SECTION UI
  const Section = ({
    title,
    sectionKey,
    children,
  }: any) => {
    const isOpen = open === sectionKey;

    return (
      <div className="w-full">
        {/* HEADER */}
        <div
          onClick={() => setOpen(isOpen ? null : sectionKey)}
          className={`
            flex justify-between items-center px-4 py-2 rounded-md border cursor-pointer
            transition-all
            ${
              isOpen
                ? "bg-blue-900 text-white"
                : "bg-white hover:bg-blue-50"
            }
          `}
        >
          <span className="font-medium">{title}</span>
          {isOpen ? <Minus size={18} /> : <Plus size={18} />}
        </div>

        {/* BODY */}
        {isOpen && (
          <div className="bg-white border shadow-md mt-2 p-4 space-y-3">
            {children}
          </div>
        )}
      </div>
    );
  };

  // CHECKBOX ROW
  const CheckRow = ({
    label,
    settingKey,
  }: {
    label: string;
    settingKey: keyof SettingsType;
  }) => (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={settings[settingKey]}
        onChange={() => toggle(settingKey)}
        className="w-4 h-4"
      />
      <span className="text-sm text-gray-700">{label}</span>
    </div>
  );

  return (
    <div className="p-6">

      {/* TITLE */}
      <div className="mb-6">
        <span className="bg-blue-900 text-white px-4 py-2 rounded-md">
          Settings
        </span>
      </div>

      {/* GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

        {/* 1. DSR */}
        <Section title="DSR Format report" sectionKey="dsr">
          <CheckRow
            label="DSR Format report"
            settingKey="dsrReport"
          />
        </Section>

        {/* 2. LIQUID */}
        <Section title="Liquid_Invoice" sectionKey="invoice">
          <CheckRow label="Add LFR Amount In Incl Rate" settingKey="lfrInclRate" />
          <CheckRow label="Add LFR Amount Product Wise Total" settingKey="lfrProductTotal" />
          <CheckRow label="Deduct Dealer comm Product Wise Total" settingKey="dealerCommission" />
          <CheckRow label="Need To Enter LFR Invoice No" settingKey="requireInvoiceNo" />
        </Section>

        {/* 3. STATEMENT */}
        <Section title="Statement Generation" sectionKey="statement">
          <CheckRow label="Add statement no" settingKey="statementNo" />
          <CheckRow label="Add Statment name" settingKey="statementName" />
          <CheckRow label="No Need Description" settingKey="noDescription" />
          <CheckRow label="No Need Misc Charges" settingKey="noMisc" />
          <CheckRow label="No Need To Show Indent No/Bill No" settingKey="noIndentBill" />
          <CheckRow label="Terms And Conditions" settingKey="terms" />
        </Section>

        {/* 4. SWIPE */}
        <Section title="Swipe Recovery" sectionKey="recovery">
          <CheckRow
            label="Show Swipe Recovery Data in Reports"
            settingKey="swipeRecovery"
          />
        </Section>

      </div>

      {/* BUTTON */}
      <div className="mt-6">
        <Button
          onClick={handleSave}
          className="bg-green-400 hover:bg-green-500 text-black px-6 rounded-full"
        >
          UPDATE SETTINGS
        </Button>
      </div>

    </div>
  );
}